import React, { Fragment, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Privilege } from '../../api';
import useSession from '../Util/useSession';
import useHasPrivilege from '../Util/useHasPrivilege';
import Progress from '../Util/Progress';
import videosService from '../../services/videos';
import SessionSessionInformation from './SessionSessionInformation';
import SessionVideosR2Upload from './Upload/SessionVideosR2Upload';
import SessionVideosEnterUrl from './Upload/SessionVideosEnterUrl';
import SessionDuplicateDialog, { sessionLink } from './SessionDuplicateDialog';
import {
  SessionLabel,
  SummaryActions,
  SummaryFacts,
  SummaryTitle,
  SummaryTitleRow,
  SummaryTop,
  SummaryWrapper,
} from '../Runs/RunStatusUi';

interface Props {
  sessionid: string;
  duplicatedFromId?: number | null;
}

const SessionUploadPage: React.FunctionComponent<Props> = ({ sessionid, duplicatedFromId }) => {
  const { session } = useSession(sessionid);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const infoRef = useRef<{ onSave: () => boolean }>();
  const [infoValid, setInfoValid] = useState(false);
  const [showUrls, setShowUrls] = useState(false);
  const [retranscoding, setRetranscoding] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const infoSaved = !!session && SessionSessionInformation.isComplete(session);
  const hasVideos = (session?.videos?.length || 0) > 0;

  const save = useCallback(() => {
    if (infoRef.current?.onSave()) {
      enqueueSnackbar('Match info saved', { variant: 'info', autoHideDuration: 2000 });
    }
  }, [enqueueSnackbar]);

  const retranscode = useCallback(async () => {
    setRetranscoding(true);
    try {
      await videosService.retranscode(sessionid);
      history.push('/session/transcode/' + sessionid);
    } finally {
      setRetranscoding(false);
    }
  }, [sessionid, history]);

  if (!session) {
    return <Progress />;
  }

  return (
    <Fragment>
      <div className="sciui-card sciui-m-l sciui-bg-p-5">
        <SummaryWrapper>
          <SummaryTop>
            <SummaryTitle>
              <SummaryTitleRow>
                <h2>
                  {session.homeTeam?.name || session.awayTeam?.name
                    ? `${session.homeTeam?.name || '?'} – ${session.awayTeam?.name || '?'}`
                    : 'New session'}
                </h2>
                {session.label && <SessionLabel>{session.label}</SessionLabel>}
              </SummaryTitleRow>
              <SummaryFacts>
                <span>Session {session.id}</span>
                {session.pitch?.name && <span>{session.pitch.name}</span>}
                {duplicatedFromId && (
                  <span>
                    Duplicate of <Link to={sessionLink(duplicatedFromId)}>#{duplicatedFromId}</Link>
                  </span>
                )}
              </SummaryFacts>
            </SummaryTitle>
            <SummaryActions>
              {hasVideos && (
                <button
                  type="button"
                  className="sciui-button sciui-button--secondary"
                  onClick={() => history.push('/session/transcode/' + sessionid)}
                >
                  Transcode status
                </button>
              )}
              {isAdmin && (
                <Fragment>
                  <button
                    type="button"
                    className="sciui-button sciui-button--secondary"
                    onClick={() => setDuplicating(true)}
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="sciui-button sciui-button--secondary"
                    onClick={retranscode}
                    disabled={retranscoding || !hasVideos}
                  >
                    {retranscoding ? <Progress /> : 'Retranscode'}
                  </button>
                </Fragment>
              )}
              <button
                type="button"
                className="sciui-button sciui-button--primary"
                onClick={() => history.push('/sessions')}
              >
                Done
              </button>
            </SummaryActions>
          </SummaryTop>
        </SummaryWrapper>
      </div>

      <Columns className="sciui-m-l">
        <div className="sciui-card sciui-bg-p-5">
          <Section>
            <SectionHeader>
              <div>
                <h2>Match</h2>
                <Hint>
                  Pitch, customer, date, competition and both teams are required. Drop an Opta F7 XML here to fill in
                  the teams.
                </Hint>
              </div>
              <button type="button" className="sciui-button sciui-button--primary" onClick={save} disabled={!infoValid}>
                Save match info
              </button>
            </SectionHeader>
            <SessionSessionInformation ref={infoRef} sessionid={sessionid} setNextEnabled={setInfoValid} />
          </Section>
        </div>

        <div className="sciui-card sciui-bg-p-5">
          <Section>
            <SectionHeader>
              <div>
                <h2>Videos</h2>
                <Hint>Uploads go straight to storage; transcoding starts when a video is done.</Hint>
              </div>
              <button
                type="button"
                className="sciui-button sciui-button--secondary"
                onClick={() => setShowUrls(!showUrls)}
              >
                {showUrls ? 'Hide URLs' : 'Add by URL'}
              </button>
            </SectionHeader>
            {infoSaved ? (
              <SessionVideosR2Upload sessionid={sessionid} setNextEnabled={() => {}} />
            ) : (
              <Locked>Save the match info first: videos are stored under the session name.</Locked>
            )}
            {showUrls && <SessionVideosEnterUrl sessionid={sessionid} setNextEnabled={() => {}} />}
          </Section>
        </div>
      </Columns>

      {isAdmin && (
        <SessionDuplicateDialog open={duplicating} sessionIds={[String(session.id)]} onClose={() => setDuplicating(false)} />
      )}
    </Fragment>
  );
};

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  gap: 24px;
  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  > div {
    flex: 1;
    min-width: 0;
  }
  > button {
    flex-shrink: 0;
  }
  h2 {
    margin: 0 0 4px;
  }
`;

const Hint = styled.div`
  color: #64748b;
  font-size: 14px;
`;

const Locked = styled.div`
  padding: 24px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
`;

export default SessionUploadPage;
